import React, { useEffect, useMemo, useState } from 'react';

import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';

import { PRODUCT_TYPES_OPTIONS, RuleCondition, RuleConditions } from '~/consts/agentHub';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
  ruleType?: keyof RuleConditions;
}

function areStringArraysEqual(left: Array<string>, right: Array<string>) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function areConditionValuesEqual(left: RuleCondition['value'], right: RuleCondition['value']) {
  return (
    left.length === right.length &&
    left.every((condition, index) => condition.value === right[index]?.value && condition.label === right[index]?.label)
  );
}

function AgentHubCommissionsProductTypeInput({ setCreatedConditions, existingConditions, ruleType }: Props) {
  const selectedFromExisting = useMemo(
    () => existingConditions.map((condition) => String(condition.value)),
    [existingConditions],
  );
  const [selectedProductType, setSelectedProductType] = useState<Array<string>>(selectedFromExisting);

  useEffect(() => {
    setSelectedProductType((currentSelectedProductType) =>
      areStringArraysEqual(currentSelectedProductType, selectedFromExisting)
        ? currentSelectedProductType
        : selectedFromExisting,
    );
  }, [selectedFromExisting]);

  useEffect(() => {
    const value = selectedProductType.map((productType) => ({
      value: productType,
      label: productType,
    }));

    if (areConditionValuesEqual(existingConditions, value)) {
      return;
    }

    setCreatedConditions(
      {
        type: ruleType || 'productType',
        value,
      },
      true,
    );
  }, [existingConditions, ruleType, selectedProductType, setCreatedConditions]);

  function handleChange(event: SelectChangeEvent<Array<string> | string>) {
    const {
      target: { value },
    } = event;

    setSelectedProductType(typeof value === 'string' ? value.split(',') : value);
  }

  function renderValueText() {
    if (selectedProductType.length === 1) {
      return selectedProductType[0];
    }

    return `${selectedProductType.length} Products Selected`;
  }

  return (
    <Stack gap={2}>
      <FormControl fullWidth>
        <InputLabel id="agent-hub-commissions-product-type-input-label">Product Type</InputLabel>
        <Select<Array<string>>
          labelId="agent-hub-commissions-product-type-input-label"
          multiple
          value={selectedProductType}
          onChange={handleChange}
          input={<OutlinedInput label="Product Type" />}
          renderValue={renderValueText}
          sx={{ borderRadius: 0.5 }}
        >
          {PRODUCT_TYPES_OPTIONS.map((productType) => (
            <MenuItem key={productType} value={productType}>
              <Checkbox checked={selectedProductType.includes(productType)} />
              <ListItemText primary={productType} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

export default AgentHubCommissionsProductTypeInput;
